import { type FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { emailValidation, simpleDropdownFieldValidation, simpleFieldValidation } from "../../app-utils/form.js";
import { type Role, type User } from "../../graphql.js";
import { AUTHENTICATION_MODE, fetchNumberOfFreeSeats } from "../../redux/modules/users-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { AddNewUserDialogImpl } from "./AddNewUserDialog.js";
import { AddNewUserToDatabaseFormImpl } from "./AddNewUserToDatabaseForm.js";
import { MAX_LENGTH } from "@/settings/settings.js";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";

interface IUserEditingFieldsProps {
  roles: Role[];
  authenticationMode?: string;
  isCreateContext?: boolean;
  user?: User;
}

const UserEditingFields: FC<IUserEditingFieldsProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const freeSeats = useSelector<IAppState, number>((state) => state.usersAdmin.numberOfFreeSeats);

  const ldapMode: boolean = props.authenticationMode === AUTHENTICATION_MODE.LDAP;
  const ldapModeTooltip: string = ldapMode
    ? "This information is managed by an external directory service and cannot be modified here"
    : null;

  // In case a user is deactivated and no free seat are left, we can't activate it.
  // You can of course still deactivate a user to give its seat to another user.
  const disabledActivation = !props.isCreateContext && !(props.user?.isActive ?? true) && (freeSeats ?? 0) === 0;

  useEffect(() => {
    void dispatch(fetchNumberOfFreeSeats());
  }, [dispatch]);

  return (
    <>
      <InputReduxFormField
        componentClass="input"
        name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_FIRSTNAME}
        label={"First name"}
        placeholder={`Enter the user's first name`}
        maxLength={MAX_LENGTH.USER.FIRSTNAME}
        component={InputField}
        disabled={ldapMode}
        tooltip={ldapModeTooltip}
        validate={!ldapMode && simpleFieldValidation}
      />

      <InputReduxFormField
        componentClass="input"
        name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_LASTNAME}
        label={"Last name"}
        placeholder={`Enter the user's last name`}
        maxLength={MAX_LENGTH.USER.LASTNAME}
        component={InputField}
        disabled={ldapMode}
        tooltip={ldapModeTooltip}
        validate={!ldapMode && simpleFieldValidation}
      />

      <InputReduxFormField
        componentClass="input"
        name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_EMAIL}
        label={"Email address"}
        placeholder={`Enter the user's email address`}
        maxLength={MAX_LENGTH.USER.EMAILADRESS}
        component={InputField}
        type={"email"}
        disabled={ldapMode}
        tooltip={ldapModeTooltip}
        validate={!ldapMode && emailValidation}
      />

      {!ldapMode && props.isCreateContext ? (
        <FormNote>User will receive an email which contains a link to set the account password.</FormNote>
      ) : null}

      <ReactSelectReduxFormField
        name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_ROLES}
        isMulti
        closeMenuOnSelect={false}
        isClearable={false}
        isSearchable={true}
        getOptionValue={(option: Role) => option.id}
        getOptionLabel={(option: Role) => option.name}
        label={"Roles"}
        tooltip={"Select one or multiple roles..."}
        placeholder={"Select one or multiple roles..."}
        component={ReactSelectField}
        options={props.roles}
        validate={simpleDropdownFieldValidation}
      />

      <ReactSelectReduxFormField
        name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_STATUS}
        label={"Account status"}
        component={ReactSelectField}
        options={AddNewUserDialogImpl.statusOptions}
        isSearchable
        isDisabled={disabledActivation}
        tooltip={disabledActivation ? "No more seats available" : undefined}
        closeMenuOnSelect
        openMenuOnFocus
        isClearable={false}
        validate={simpleDropdownFieldValidation}
      />
    </>
  );
};

export default UserEditingFields;
